/**
 * AccessibilityMenu — header dropdown replacing the old "Senior" pill.
 * Contains: Senior Mode switch + Text size (A− / A / A+) controls.
 * Text size persists in localStorage (vt_text_scale) and is applied as an
 * inline root font-size (overrides the senior-mode 18px class when set).
 */
import { useEffect, useState } from "react";
import { Accessibility, ChevronDown, ZoomIn, ZoomOut } from "lucide-react";
import { useSeniorMode } from "@/contexts/SeniorModeContext";
import { useLang } from "@/contexts/LanguageContext";
import { Switch } from "@/components/ui/switch";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SCALE_KEY = "vt_text_scale";
const SCALES = [
    { id: "sm", label: "A−", px: "15px" },
    { id: "base", label: "A", px: "" },
    { id: "lg", label: "A+", px: "18px" },
];

const applyScale = (id) => {
    const s = SCALES.find((x) => x.id === id) || SCALES[1];
    try { document.documentElement.style.fontSize = s.px; } catch { /* SSR */ }
};

export function AccessibilityMenu({ compact = false, short = false }) {
    const { enabled, toggle } = useSeniorMode();
    const { t } = useLang();
    const [scale, setScale] = useState(() => {
        try { return localStorage.getItem(SCALE_KEY) || "base"; } catch { return "base"; }
    });

    useEffect(() => { applyScale(scale); }, [scale]);

    const pick = (id) => {
        setScale(id);
        try { localStorage.setItem(SCALE_KEY, id); } catch { /* quota */ }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid="accessibility-menu-trigger"
                aria-label="Accessibility options"
                title={t("accMenuLabel")}
                className={`inline-flex items-center gap-1.5 rounded-full border font-semibold transition-colors focus:outline-none ${
                    short ? "h-10 w-11 justify-center px-0 text-[15px]" : `h-9 text-[13px] ${compact ? "px-2.5" : "px-3"}`
                } ${
                    enabled || scale !== "base"
                        ? "bg-[#1B5E20] text-white border-[#1B5E20] shadow-[0_6px_16px_rgba(27,94,32,0.28)]"
                        : "bg-white text-[#111827] border-[#D1D5DB] hover:bg-[#F9FAFB]"
                }`}
            >
                {short ? (
                    <span className="font-bold">A+</span>
                ) : (
                    <>
                        <Accessibility className="h-4 w-4" />
                        <span className={compact ? "hidden xl:inline" : ""}>{t("accMenuLabel")}</span>
                        <ChevronDown className="h-3 w-3 opacity-70" />
                    </>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                sideOffset={10}
                data-testid="accessibility-menu"
                className="w-72 rounded-2xl border-[#E3E0D3] shadow-[0_18px_50px_rgba(15,51,17,0.14)] p-4 bg-white space-y-4"
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[13.5px] font-bold text-[#0F3311]">{t("accSenior")}</p>
                        <p className="text-[12px] text-[#4A5D4E] mt-0.5 leading-snug">{t("accSeniorDesc")}</p>
                    </div>
                    <Switch
                        data-testid="senior-mode-toggle"
                        checked={enabled}
                        onCheckedChange={toggle}
                        aria-label="Toggle Senior Mode"
                        className="data-[state=checked]:bg-[#1B5E20] shrink-0 mt-0.5"
                    />
                </div>
                <div className="h-px bg-[#E3E0D3]" />
                <div>
                    <p className="text-[13.5px] font-bold text-[#0F3311] mb-2">{t("accTextSize")}</p>
                    <div className="grid grid-cols-3 gap-2">
                        {SCALES.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => pick(s.id)}
                                data-testid={`accessibility-text-${s.id}`}
                                aria-pressed={scale === s.id}
                                className={`h-10 rounded-xl border font-bold transition-colors ${
                                    s.id === "sm" ? "text-[13px]" : s.id === "lg" ? "text-[17px]" : "text-[15px]"
                                } ${
                                    scale === s.id
                                        ? "bg-[#1B5E20] text-white border-[#1B5E20]"
                                        : "bg-white text-[#0F3311] border-[#E3E0D3] hover:bg-[#F8FAF7]"
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default AccessibilityMenu;
